import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from '../../../helpers/axios'
import SiteMediaViewer from '../../../components/media/SiteMediaViewer'
import './OfficeCatalogEvidenceMediaAO.scss'

// These are PRIVATE photos of old printed price lists, not Product.productPictures.
// Always request them through the authenticated office endpoint.
const previewCache = new Map()
const MAX_PREVIEW_CACHE = 120

const getMeta = (product, meta) => meta || product?.officeCatalogImport || null
const getPhotoIds = (meta) => Array.isArray(meta?.sourcePhotos)
    ? [...new Set(meta.sourcePhotos.map((id) => String(id || '').trim()).filter(Boolean))]
    : []

const loadSourceBlob = async ({ officeId, catalogKey, photoId, localPhotoMap }) => {
    const localFile = localPhotoMap?.get(String(photoId))
    if (localFile instanceof Blob) return localFile

    const response = await axios.get(
        `/office/catalog-json/source/${encodeURIComponent(catalogKey)}/${encodeURIComponent(photoId)}`,
        { params: { officeId }, responseType: 'blob' }
    )
    if (!(response.data instanceof Blob) || !response.data.size) {
        throw new Error('Изображение ценника не найдено')
    }
    return response.data
}

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 5000)
}

// The table may contain hundreds of rows referencing the same 55 originals.
// Decode each *visible* source only once into a tiny in-memory preview.
const loadSmallPreview = (officeId, catalogKey, photoId) => {
    const key = `${officeId}:${catalogKey}:${photoId}`
    const cached = previewCache.get(key)
    if (cached) return cached

    const task = (async () => {
        const blob = await loadSourceBlob({ officeId, catalogKey, photoId })
        const bitmap = await createImageBitmap(blob)
        try {
            const maxSide = 280
            const scale = Math.min(maxSide / bitmap.width, maxSide / bitmap.height, 1)
            const canvas = document.createElement('canvas')
            canvas.width = Math.max(1, Math.round(bitmap.width * scale))
            canvas.height = Math.max(1, Math.round(bitmap.height * scale))
            const ctx = canvas.getContext('2d')
            if (!ctx) throw new Error('Не удалось обработать миниатюру')
            ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
            return canvas.toDataURL('image/jpeg', 0.72)
        } finally {
            bitmap.close()
        }
    })() // A failed lookup stays cached for this session to avoid repeated 404s.

    previewCache.set(key, task)
    while (previewCache.size > MAX_PREVIEW_CACHE) {
        previewCache.delete(previewCache.keys().next().value)
    }
    return task
}

export function EvidenceViewer({ officeId, catalogKey, photoIds = [], localPhotoMap, onClose }) {
    const signature = (photoIds || []).map(String).join('|')
    const items = useMemo(() => signature.split('|').filter(Boolean).map((photoId) => ({ photoId })), [signature])

    const resolveItem = useCallback(async (item) => {
        if (!officeId || !catalogKey || !item?.photoId) throw new Error('Источник не указан')
        try {
            const blob = await loadSourceBlob({ officeId, catalogKey, photoId: item.photoId, localPhotoMap })
            return { blob, mimeType: blob.type || 'image/jpeg', filename: `${item.photoId}.jpg` }
        } catch (error) {
            throw new Error(error?.response?.data?.message || 'Фото ценника не загружено на сервер. Проверь исходный архив.')
        }
    }, [officeId, catalogKey, localPhotoMap])

    const handleDownload = useCallback(async (item) => {
        try {
            const blob = await loadSourceBlob({ officeId, catalogKey, photoId: item.photoId, localPhotoMap })
            const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg'
            downloadBlob(blob, `Ценник_${item.photoId}.${ext}`)
        } catch (error) {
            // The media viewer will still allow retrying the preview; never expose a private URL.
            window.alert(error?.response?.data?.message || 'Не удалось скачать исходный ценник')
        }
    }, [officeId, catalogKey, localPhotoMap])

    if (!items.length) return null

    return (
        // React events in a portal still bubble through the logical parent (a table row).
        // Avoid opening the underlying product card when using the viewer controls.
        <span onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
          <SiteMediaViewer
            open
            items={items}
            onClose={onClose}
            resolveItem={resolveItem}
            getItemMimeType={() => 'image/jpeg'}
            getTitle={() => 'Фото исходного ценника'}
            getSubtitle={(item, index) => `Источник № ${item.photoId} · ${index + 1} / ${items.length} · доступно только сотрудникам офиса`}
            getAlt={(item) => `Исходный ценник № ${item.photoId}`}
            onDownload={handleDownload}
            downloadLabel="Скачать"
            loadingText="Загружаем исходный ценник…"
            emptyText="Фото ценника отсутствует"
          />
        </span>
    )
}

export function OfficeCatalogSourceButtonAO({ product, officeId }) {
    const [open, setOpen] = useState(false)
    const meta = getMeta(product)
    const signature = getPhotoIds(meta).join('|')
    const ids = useMemo(() => signature.split('|').filter(Boolean), [signature])
    if (!officeId || !meta?.catalogKey || !ids.length) return null

    return (
        <>
            <button
                type="button"
                className="office-import-AO__sourceLink"
                onClick={(event) => { event.stopPropagation(); setOpen(true) }}
                title="Посмотреть исходные ценники (не фотографии товаров)"
            >
                Фото ценника · {ids.length}
            </button>
            {open && (
                <EvidenceViewer
                    officeId={officeId}
                    catalogKey={meta.catalogKey}
                    photoIds={ids}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    )
}

// Office-only fallback in product table and office quick preview. Never use on public storefront / VK.
export function OfficeCatalogSourceThumbnailAO({ product, meta: providedMeta, officeId }) {
    const meta = getMeta(product, providedMeta)
    const signature = getPhotoIds(meta).join('|')
    const ids = useMemo(() => signature.split('|').filter(Boolean), [signature])
    const photoId = ids[0]
    const catalogKey = String(meta?.catalogKey || '')
    const previewRef = useRef(null)
    const [inView, setInView] = useState(false)
    const [src, setSrc] = useState('')
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!officeId || !catalogKey || !photoId) return undefined
        const node = previewRef.current
        if (!node || typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return undefined
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setInView(true)
                observer.disconnect()
            }
        }, { rootMargin: '100px' })
        observer.observe(node)
        return () => observer.disconnect()
    }, [officeId, catalogKey, photoId])

    useEffect(() => {
        setSrc('')
        if (!inView || !officeId || !catalogKey || !photoId) return undefined
        let alive = true
        loadSmallPreview(officeId, catalogKey, photoId)
            .then((url) => { if (alive) setSrc(url) })
            .catch(() => { if (alive) setSrc('') })
        return () => { alive = false }
    }, [inView, officeId, catalogKey, photoId])

    if (!officeId || !catalogKey || !photoId) return null
    return (
        <>
            <button
                ref={previewRef}
                className="office-catalog-source-preview-AO"
                type="button"
                onClick={(event) => { event.stopPropagation(); setOpen(true) }}
                title="Открыть исходный ценник в полноэкранном просмотрщике"
                aria-label="Открыть фото исходного ценника"
            >
                {src ? <img src={src} alt="Миниатюра исходного ценника" /> : <span className="office-catalog-source-preview-AO__placeholder">Ценник</span>}
                <span className="office-catalog-source-preview-AO__label">Источник прайса</span>
            </button>
            {open && (
                <EvidenceViewer
                    officeId={officeId}
                    catalogKey={catalogKey}
                    photoIds={ids}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    )
}
